import * as React from "react";

interface HourlyRateProps {
  rate: number;
  onRateChange: (rate: number) => void;
  currency: "RON" | "EUR";
}

const HourlyRate: React.FC<HourlyRateProps> = ({ rate, onRateChange, currency }) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [editValue, setEditValue] = React.useState<number>(rate);

  React.useEffect(() => {
    if (!isEditing) setEditValue(rate);
  }, [rate, isEditing]);

  const handleIncrease = () => onRateChange(rate + 1);
  const handleDecrease = () => onRateChange(Math.max(0, rate - 1));


  const saveEdit = () => {
    if (Number.isFinite(editValue) && editValue >= 0) {
      onRateChange(Number(editValue));
    }
    setIsEditing(false);
  };

  const handleInputBlur = () => saveEdit();
  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") saveEdit();
    else if (e.key === "Escape") setIsEditing(false);
  };

  return (
    <div className="flex items-center justify-between bg-gray-800 text-white rounded-lg p-6 w-full">
      <div className="flex items-center justify-between m-auto w-full sm:w-3/4 md:w-1/2">
        <button
          onClick={handleDecrease}
          className="font-ubuntu font-bold text-2xl w-10 h-10 flex items-center justify-center bg-gray-700 rounded-full hover:bg-gray-600 transition-colors"
        >
          -
        </button>
        <div className="text-center font-ubuntu flex items-center">
          {isEditing ? (
            <input
              type="number"
              step="1"
              className="text-4xl font-bold text-center text-black bg-white rounded px-2 w-20 outline-none"
              value={editValue === 0 ? "" : editValue}
              onFocus={e => {
                if (editValue === 0) setEditValue(NaN);
                e.target.select();
              }}
              onChange={e => {
                // leading 0 or empty string: treat as ""
                if (/^0\d*$/.test(e.target.value)) {
                  setEditValue(Number(e.target.value.replace(/^0+/, "")));
                } else {
                  setEditValue(Number(e.target.value));
                }
              }}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              autoFocus
              min={0}
              placeholder="0"
            />
          ) : (
            <span
              className="text-4xl font-bold text-white cursor-pointer hover:text-blue-300 transition"
              tabIndex={0}
              onClick={() => setIsEditing(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setIsEditing(true);
              }}
            >
              {rate}
            </span>
          )}
          <span className="text-xl ml-2">{currency}</span>
          <span className="text-xl ml-2">/hour</span>
        </div>
        <button
          onClick={handleIncrease}
          className="font-ubuntu font-bold text-2xl w-10 h-10 flex items-center justify-center bg-gray-700 rounded-full hover:bg-gray-600 transition-colors"
        >
          +
        </button>
      </div>
    </div>
  );
};

export default HourlyRate;
